import { supabase } from '../lib/supabase';

import { uploadCaseFile, getCaseFileUrl } from './storageService';
import { logAuditEvent } from './auditLogService';

export async function createCase(userId, caseData) {
  try {
    // case_number is now set automatically by the trg_set_case_number BEFORE INSERT trigger
    const { data, error } = await supabase
      ?.from('cases')
      ?.insert({
        user_id: userId,
        persona: caseData?.persona,
        status: 'draft',
        incident_date: caseData?.incidentDate,
        incident_time: caseData?.incidentTime || null,
        property_address: caseData?.propertyAddress,
        property_type: caseData?.propertyType,
        allegation_type: caseData?.allegationType,
        claim_number: caseData?.claimNumber || null,
        case_reference_number: caseData?.caseNumber || null,
        criminal_case_number: caseData?.criminalCaseNumber || null,
        estimated_loss: caseData?.estimatedLoss || null,
        additional_context: caseData?.additionalContext || null
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Log audit event
    await logAuditEvent({
      actionType: 'case_create',
      actionDescription: `Created case ${data?.case_number}`,
      entityType: 'case',
      entityId: data?.id,
      severity: 'info'
    });

    return { success: true, data };
  } catch (error) {
    console.error('Create case error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getSignedUrl(storagePath, bucket = 'case-images') {
  return getCaseFileUrl(storagePath, bucket);
}

export async function checkAvailableCredits(userId) {
  try {
    const { data, error } = await supabase?.rpc('check_available_credits', {
      p_user_id: userId
    });

    if (error) throw error;

    return { success: true, credits: data || 0 };
  } catch (error) {
    console.error('Check credits error:', error);
    return { success: false, error: error?.message };
  }
}

export async function consumeCredit(userId, caseId) {
  try {
    const { data, error } = await supabase?.rpc('consume_credit', {
      p_user_id: userId,
      p_case_id: caseId
    });

    if (error) throw error;

    return { success: true, creditType: data };
  } catch (error) {
    console.error('Consume credit error:', error);
    return { success: false, error: error?.message };
  }
}

export async function updateCaseStatus(caseId, status, additionalData = {}) {
  try {
    const updateData = {
      status,
      ...additionalData
    };

    if (status === 'intake_submitted') {
      updateData.submitted_at = new Date()?.toISOString();
    } else if (status === 'report_ready') {
      updateData.report_generated_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase
      ?.from('cases')
      ?.update(updateData)
      ?.eq('id', caseId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Update case status error:', error);
    return { success: false, error: error?.message };
  }
}

export async function saveClarifyingQuestions(caseId, questions, round = 1) {
  try {
    const questionsData = questions?.map((q, index) => ({
      case_id: caseId,
      question_text: q?.question,
      priority: q?.priority,
      needs_image: q?.needs_image ?? false,
      display_order: index,
      round
    }));

    const { data, error } = await supabase
      ?.from('clarifying_questions')
      ?.insert(questionsData)
      ?.select();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Save questions error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getClarifyingQuestions(caseId) {
  try {
    const { data, error } = await supabase
      ?.from('clarifying_questions')
      ?.select('*')
      ?.eq('case_id', caseId)
      ?.order('round', { ascending: true })
      ?.order('display_order', { ascending: true });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get questions error:', error);
    return { success: false, error: error?.message };
  }
}

// Returns only the questions from the latest round (for display)
export async function getLatestRoundQuestions(caseId) {
  try {
    const { data: allQ, error } = await supabase
      ?.from('clarifying_questions')
      ?.select('*')
      ?.eq('case_id', caseId)
      ?.order('round', { ascending: false })
      ?.order('display_order', { ascending: true });

    if (error) throw error;
    if (!allQ?.length) return { success: true, data: [], round: 1 };

    const latestRound = allQ[0]?.round;
    const latestQuestions = allQ.filter(q => q.round === latestRound);
    return { success: true, data: latestQuestions, round: latestRound };
  } catch (error) {
    console.error('Get latest round questions error:', error);
    return { success: false, error: error?.message };
  }
}

export async function saveQuestionAnswer(questionId, answer) {
  try {
    const { data, error } = await supabase
      ?.from('clarifying_questions')
      ?.update({
        answer_text: answer,
        answered_at: new Date()?.toISOString()
      })
      ?.eq('id', questionId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Save answer error:', error);
    return { success: false, error: error?.message };
  }
}

// Centralized AI pricing — must match OpenAI's actual rates
// gpt-4o: $5 / 1M input, $15 / 1M output
// gpt-5.2: $10 / 1M input, $30 / 1M output
export const AI_MODEL_PRICING = {
  'gpt-4o': { input: 5 / 1_000_000, output: 15 / 1_000_000 },
  'gpt-5.2': { input: 10 / 1_000_000, output: 30 / 1_000_000 },
};

export function calculateAIRunCost(usage, model) {
  if (!usage) return 0;
  const rate = AI_MODEL_PRICING?.[model] || AI_MODEL_PRICING['gpt-4o'];
  return (usage?.prompt_tokens || 0) * rate.input + (usage?.completion_tokens || 0) * rate.output;
}

export async function saveAIRun(caseId, phase, model, usage, responseData, errorMessage = null) {
  try {
    const totalCost = calculateAIRunCost(usage, model);

    const { data, error } = await supabase
      ?.from('ai_runs')
      ?.insert({
        case_id: caseId,
        phase,
        model,
        prompt_tokens: usage?.prompt_tokens || 0,
        completion_tokens: usage?.completion_tokens || 0,
        total_tokens: usage?.total_tokens || 0,
        cost_usd: totalCost,
        response_data: responseData,
        error_message: errorMessage
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Save AI run error:', error);
    return { success: false, error: error?.message };
  }
}

export async function saveReport(caseId, reportData, pdfUrl = null) {
  try {
    // Use upsert so that report generation retries overwrite the previous
    // (possibly incomplete) record instead of failing on a UNIQUE constraint.
    const { data, error } = await supabase
      ?.from('reports')
      ?.upsert(
        {
          case_id: caseId,
          report_data: reportData,
          pdf_url: pdfUrl
        },
        { onConflict: 'case_id' }
      )
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Save report error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getCaseById(caseId) {
  try {
    const { data, error } = await supabase
      ?.from('cases')
      ?.select('*')
      ?.eq('id', caseId)
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Get case error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getUserCases(userId) {
  try {
    const { data, error } = await supabase
      ?.from('cases')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get user cases error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getUserSubscription(userId) {
  try {
    const { data, error } = await supabase
      ?.from('user_subscriptions')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        const { data: newSub, error: createError } = await supabase
          ?.from('user_subscriptions')
          ?.insert({
            user_id: userId,
            plan_type: 'free_trial',
            trial_credits: 1,
            paid_credits: 0,
            monthly_credits: 0
          })
          ?.select()
          ?.single();

        if (createError) throw createError;
        return { success: true, data: newSub };
      }
      throw error;
    }

    return { success: true, data };
  } catch (error) {
    console.error('Get subscription error:', error);
    return { success: false, error: error?.message };
  }
}

export async function finalizeCase(caseId) {
  try {
    const { data, error } = await supabase
      ?.from('cases')
      ?.update({
        is_submitted: true,
        submission_finalized_at: new Date()?.toISOString()
      })
      ?.eq('id', caseId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Log audit event
    await logAuditEvent({
      actionType: 'case_submit',
      actionDescription: `Finalized case submission`,
      entityType: 'case',
      entityId: caseId,
      severity: 'info'
    });

    return { success: true, data };
  } catch (error) {
    console.error('Finalize case error:', error);
    return { success: false, error: error?.message };
  }
}

export async function createSimilarCase(parentCaseId, userId) {
  try {
    // Get parent case data
    const { data: parentCase, error: fetchError } = await supabase
      ?.from('cases')
      ?.select('*')
      ?.eq('id', parentCaseId)
      ?.single();

    if (fetchError) throw fetchError;

    // Create new case with parent reference
    const { data: caseNumber } = await supabase?.rpc('generate_case_number');

    const { data, error } = await supabase
      ?.from('cases')
      ?.insert({
        user_id: userId,
        case_number: caseNumber,
        parent_case_id: parentCaseId,
        persona: parentCase?.persona,
        status: 'draft',
        incident_date: parentCase?.incident_date,
        incident_time: parentCase?.incident_time,
        property_address: parentCase?.property_address,
        property_type: parentCase?.property_type,
        allegation_type: parentCase?.allegation_type,
        claim_number: parentCase?.claim_number,
        case_reference_number: parentCase?.case_reference_number,
        criminal_case_number: parentCase?.criminal_case_number,
        estimated_loss: parentCase?.estimated_loss,
        additional_context: parentCase?.additional_context
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Log audit event
    await logAuditEvent({
      actionType: 'case_revision',
      actionDescription: `Created revision case ${caseNumber} from parent case`,
      entityType: 'case',
      entityId: data?.id,
      metadata: { parent_case_id: parentCaseId },
      severity: 'info'
    });

    return { success: true, data };
  } catch (error) {
    console.error('Create similar case error:', error);
    return { success: false, error: error?.message };
  }
}
export { uploadCaseFile };